// notifications.ts
import { API_BASE, getAuthHeaders } from './config';

export type InactiveSegment =
  | 'inactive_7_14'
  | 'inactive_14_30'
  | 'inactive_30_60'
  | 'inactive_gt_60'
  | 'all';

export type InactiveBuckets = {
  total_users?: number;
  inactive_7_14: number;
  inactive_14_30: number;
  inactive_30_60: number;
  inactive_gt_60: number;
};

export type TargetEstimate = {
  total: number;
  emailEligible: number;
  localEligible: number;
};

export type EmailTemplateListItem = {
  id: number;
  tkey: string;
  name: string;
  subject: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type Overview = {
  total_users: number;
  eligible_local: number;
  eligible_email: number;
};

export type AgeBuckets = {
  age_0_6: number;
  age_7_12: number;
  age_13_17: number;
  age_18_plus: number;
};

export type PremiumStats = {
  active_total: number;
  expiring_7d_total: number;
  expired_total: number;
  active_basic: number;
  active_standard: number;
  active_family: number;
  expired_basic: number;
  expired_standard: number;
  expired_family: number;
  never_total: number;
};

export type PushData = {
  type?: string;
  context?: 'activity' | 'premium' | 'general' | 'legal';
  screen?: string;
  params?: Record<string, any>;
};

export type SendPayload = {
  title: string;
  message: string;
  longMessage?: string;
  segment?: InactiveSegment;
  userIds?: number[];
  ageBucket?: '0_6' | '7_12' | '13_17' | '18_plus';
  premium?: {
    status: 'active' | 'expired' | 'expiring_7d' | 'never';
    plans?: Array<'BASIC' | 'STANDARD' | 'FAMILY'>;
  };
  /** czy zapisać in-app (tabela notifications) */
  sendLocal?: boolean;
  /** czy wysłać prostego maila (bez templata) */
  sendEmail?: boolean;
  /** czy oprócz in-app wysłać też push przez Expo */
  sendPush?: boolean;
  /** dane dla klienta mobilnego do nawigacji po kliknięciu */
  pushData?: PushData;
  /**
   * Jeśli true – zignoruje accepts_email_notifications w backendzie
   * i wyśle maila do wszystkich z targetu (must-have: Nowości krytyczne, Regulamin).
   * Jeśli false/undefined – tylko do użytkowników ze zgodą.
   */
  forceAllEmails?: boolean;
  /**
   * Jeśli true – zapisze in-app notification dla wszystkich z targetu,
   * ignorując accepts_local_notifications (lista w appce, ważne info / aktywność).
   * Jeśli false/undefined – respektuje accepts_local_notifications.
   */
  forceLocalAll?: boolean;
};

export type SendResponse = {
  insertedLocal: number;
  insertedNotificationIds: number[];
  sentEmail: number;
  emailErrors: Array<{ userId: number; error: string }>;
  /** opcjonalnie z backendu */
  sentPush?: number;
  pushErrors?: Array<{
    userId: number | null;
    token: string | null;
    error: string;
    details?: any;
  }>;
};

export type RecommendResponse = {
  results: Array<{ userId: number; notificationId: number; count: number }>;
};

export type TemplateSendPayload = {
  templateKey: string;
  vars?: Record<string, any>;
  segment?: InactiveSegment;
  userIds?: number[];
  ageBucket?: '0_6' | '7_12' | '13_17' | '18_plus';
  premium?: {
    status: 'active' | 'expired' | 'expiring_7d' | 'never';
    plans?: Array<'BASIC' | 'STANDARD' | 'FAMILY'>;
  };
  /**
   * Jeśli true – backend w /admin/notifications/send-email
   * zignoruje accepts_email_notifications (must-have: Regulamin, krytyczne Nowości).
   * Jeśli false/undefined – tylko do userów ze zgodą marketingową.
   */
  forceAllEmails?: boolean;
  subjectOverride?: string;
};

export type TemplateSendResponse = {
  sentEmail: number;
  emailErrors: Array<{ userId: number; error: string }>;
};

export type PublishLegalPayload = {
  change: 'TERMS' | 'PRIVACY' | 'BOTH';
  version?: string; // opcjonalnie np. '2025-09-10'
};

export type PublishLegalResponse = {
  ok: boolean;
  terms: { id: number; version: string; published_at: string } | null;
  privacy: { id: number; version: string; published_at: string } | null;
};

/** ---------- ANALYTICS ---------- */
export const fetchOverview = async (): Promise<Overview> => {
  const res = await fetch(`${API_BASE}/admin/analytics/overview`, { headers: getAuthHeaders() });
  if (!res.ok) throw await res.json();
  return res.json();
};

export const fetchInactiveBuckets = async (): Promise<InactiveBuckets> => {
  const res = await fetch(`${API_BASE}/admin/analytics/inactive-users`, { headers: getAuthHeaders() });
  if (!res.ok) throw await res.json();
  return res.json();
};

export const fetchAgeBuckets = async (): Promise<AgeBuckets> => {
  const res = await fetch(`${API_BASE}/admin/analytics/ages`, { headers: getAuthHeaders() });
  if (!res.ok) throw await res.json();
  return res.json();
};

export const fetchPremiumStats = async (): Promise<PremiumStats> => {
  const res = await fetch(`${API_BASE}/admin/analytics/premium`, { headers: getAuthHeaders() });
  if (!res.ok) throw await res.json();
  return res.json();
};

export const estimateTarget = async (payload: {
  userIds?: number[];
  segment?: InactiveSegment;
  ageBucket?: '0_6' | '7_12' | '13_17' | '18_plus';
  premium?: { status: 'active' | 'expired' | 'expiring_7d' | 'never'; plans?: Array<'BASIC' | 'STANDARD' | 'FAMILY'> };
}): Promise<TargetEstimate> => {
  const res = await fetch(`${API_BASE}/admin/analytics/estimate-target`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await res.json();
  return res.json();
};

/** ---------- EMAIL TEMPLATES ---------- */
export const fetchEmailTemplates = async (): Promise<EmailTemplateListItem[]> => {
  const res = await fetch(`${API_BASE}/admin/email-templates`, { headers: getAuthHeaders() });
  if (!res.ok) throw await res.json();
  const data = await res.json();
  return data.items as EmailTemplateListItem[];
};

export type EmailTemplateMeta = {
  tkey: string;
  name: string;
  subject: string;
  html?: string;
  text?: string | null;
  is_active?: boolean;
  placeholders?: Array<
    string | { key: string; label?: string; type?: 'text' | 'url' | 'list' | 'longtext'; required?: boolean }
  >;
  defaults?: Record<string, any>;
};

export const fetchEmailTemplateMeta = async (tkey: string): Promise<EmailTemplateMeta> => {
  const res = await fetch(`${API_BASE}/admin/email-templates/${tkey}`, { headers: getAuthHeaders() });
  if (!res.ok) throw await res.json();
  return res.json();
};

export const updateEmailTemplate = async (
  tkey: string,
  patch: Partial<Pick<EmailTemplateMeta, 'name' | 'subject' | 'html' | 'text' | 'is_active' | 'placeholders' | 'defaults'>>
): Promise<{ updated: number }> => {
  const res = await fetch(`${API_BASE}/admin/email-templates/${tkey}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw await res.json();
  return res.json();
};

/** ---------- SENDS ---------- */
export const sendNotification = async (payload: SendPayload): Promise<SendResponse> => {
  const res = await fetch(`${API_BASE}/admin/notifications/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await res.json();
  return res.json();
};

export const sendEmailUsingTemplate = async (payload: TemplateSendPayload): Promise<TemplateSendResponse> => {
  const res = await fetch(`${API_BASE}/admin/notifications/send-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({
      templateKey: payload.templateKey,
      vars: payload.vars || {},
      userIds: payload.userIds,
      segment: payload.segment,
      ageBucket: payload.ageBucket,
      premium: payload.premium,
      forceAllEmails: !!payload.forceAllEmails,
      subjectOverride: payload.subjectOverride,
    }),
  });
  if (!res.ok) throw await res.json();
  return res.json();
};

export const sendRecommendations = async (
  params: { userId?: number; limit?: number; ageBucket?: '0_6' | '7_12' | '13_17' | '18_plus' } = {}
): Promise<RecommendResponse> => {
  const res = await fetch(`${API_BASE}/admin/notifications/recommend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(params),
  });
  if (!res.ok) throw await res.json();
  return res.json();
};

export const publishLegal = async (payload: PublishLegalPayload): Promise<PublishLegalResponse> => {
  const res = await fetch(`${API_BASE}/admin/legal/publish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await res.json();
  return res.json();
};
